<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { type Product, formatEuros } from '../../api/catalog';

const props = defineProps<{ items: Product[] }>();
const router = useRouter();

const active = ref(0);

// Center the carousel once items arrive.
watch(
  () => props.items.length,
  (n) => { if (n) active.value = Math.floor(n / 2); },
  { immediate: true },
);

// Wrap-around stepping — never hits an end, so the loop feels endless.
function step(dir: number) {
  const n = props.items.length;
  if (!n) return;
  active.value = (active.value + dir + n) % n;
}
function prev() { step(-1); restartAuto(); }
function next() { step(1); restartAuto(); }

// Clicking a side card brings it to the centre first; only the centred card
// opens the product. We fully own the click (router-link `custom`), so this is
// deterministic regardless of RouterLink's internal handler ordering.
function onCardClick(i: number, e: MouseEvent) {
  e.preventDefault();
  if (i === active.value) {
    router.push(`/product/${props.items[i].id}`);
    return;
  }
  active.value = i;
  restartAuto();
}

// Coverflow transform per card, using the SHORTEST circular distance to the
// active card so neighbours always wrap in on both sides (infinite illusion).
function cardStyle(i: number) {
  const n = props.items.length;
  let offset = i - active.value;
  if (n) {
    if (offset > n / 2) offset -= n;
    else if (offset < -n / 2) offset += n;
  }
  const abs = Math.abs(offset);
  const scale = abs === 0 ? 1 : abs === 1 ? 0.86 : 0.74;
  const opacity = abs > 3 ? 0 : abs === 0 ? 1 : abs === 1 ? 0.9 : 0.55;
  return {
    transform: `translateX(calc(-50% + ${offset * 150}px)) scale(${scale})`,
    opacity: String(opacity),
    zIndex: String(50 - abs),
    pointerEvents: abs > 3 ? 'none' : 'auto',
  } as Record<string, string>;
}

// Gentle autoplay so the loop reads as continuously moving; pauses on hover.
const AUTO_MS = 3200;
let timer: number | undefined;
function startAuto() { stopAuto(); timer = window.setInterval(() => step(1), AUTO_MS); }
function stopAuto() { if (timer) { clearInterval(timer); timer = undefined; } }
function restartAuto() { if (timer) startAuto(); } // reset cadence only while running

onMounted(startAuto);
onUnmounted(stopAuto);
</script>

<template>
  <div class="relative h-[360px] w-full" @mouseenter="stopAuto" @mouseleave="startAuto">
    <!-- Cards -->
    <div
      v-for="(item, i) in items"
      :key="item.id"
      class="absolute left-1/2 top-4 w-[180px] transition-all duration-500 ease-out"
      :style="cardStyle(i)"
    >
      <router-link :to="`/product/${item.id}`" custom v-slot="{ href }">
      <a
        :href="href"
        class="flex flex-col gap-2 rounded-2xl border bg-surface p-2.5 transition-colors"
        :class="i === active ? 'border-accent shadow-[0_0_40px_-8px_rgba(240,86,26,0.7)]' : 'border-line'"
        @click="onCardClick(i, $event)"
      >
        <div class="relative overflow-hidden rounded-xl bg-surface-2">
          <img v-if="item.coverUrl" :src="item.coverUrl" :alt="item.name" class="aspect-[3/4] w-full object-cover" />
          <div v-else class="grid aspect-[3/4] place-items-center text-[10px] uppercase text-muted">{{ $t('common.noImage') }}</div>
          <span class="absolute bottom-2 left-2 rounded-full border border-accent/70 bg-bg/80 px-2.5 py-0.5 font-display text-[9px] uppercase tracking-wider text-ink backdrop-blur-sm">{{ $t('common.baseGame') }}</span>
        </div>
        <h3 class="line-clamp-1 font-display text-[11px] uppercase text-white">{{ item.name }}</h3>
        <p class="text-[11px] text-muted">{{ $t('common.startingAt') }} <span class="font-semibold text-accent">{{ formatEuros(item.ourPriceCents) }}</span></p>
        <div class="flex flex-wrap gap-1.5">
          <span v-if="item.platform" class="rounded-full border border-line px-2 py-0.5 text-[9px] text-muted">{{ item.platform }}</span>
          <span v-for="g in item.genres.slice(0, 2)" :key="g" class="rounded-full border border-line px-2 py-0.5 text-[9px] text-muted">{{ g }}</span>
        </div>
      </a>
      </router-link>
    </div>

    <!-- Arrows -->
    <button
      class="absolute left-2 top-1/2 z-[60] grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-line bg-bg/70 text-ink backdrop-blur transition hover:border-accent/60"
      aria-label="Previous"
      @click="prev"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6" /></svg>
    </button>
    <button
      class="absolute right-2 top-1/2 z-[60] grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-accent text-white shadow-[0_0_24px_-6px_rgba(240,86,26,0.9)] transition hover:bg-accent-2"
      aria-label="Next"
      @click="next"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6" /></svg>
    </button>
  </div>
</template>
